import { TFunction } from 'i18next';
import React from 'react';
import { IconCell } from 'src/common/components/ConfigurableTable/Components/Cells/IconCell';
import { SimTagsCell } from 'src/common/components/ConfigurableTable/Components/Cells/SimTagsCell';
import { SubscriptionStatusCell } from 'src/common/components/ConfigurableTable/Components/Cells/SubscriptionStatusCell';
import { IccidImsiCell } from 'src/simInventory/Components/ConfigurableTable/tableCells/IccidImsiCell';
import { SessionStatusIndicator } from 'src/simInventory/Components/SessionStatusIndicator';
import { getSessionStatusTranslatedLabel } from 'src/simInventory/utils/sessionStatus';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell';
import { AnyAbility } from '@casl/ability';
import { getHuminizeDurationInSecFunction } from 'src/i18n/dateLocale';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';
import { useIsSessionDetailsListDetailsLoading } from 'src/simInventory/hooks/useSessionListDetails';
import { ColumnConfiguration } from '../constants/columnConfigurationsConstants';

type GetHeaderTitleArgs = { isChildren?: boolean; t: TFunction };
type GetHeaderTitleFunc = (args: GetHeaderTitleArgs) => string;

type ColumnsDef = {
  width?: number;
  maxWidth?: number;
  getHeaderTitle: GetHeaderTitleFunc;
  getCellText?: (args: { value: unknown }) => string | undefined | unknown;
  cellRender?: (args: {
    column: ColumnConfiguration;
    row: any;
    ability: AnyAbility;
  }) => React.ReactNode;
};

const humanize = getHuminizeDurationInSecFunction();

const getCellText = (value: unknown) => {
  if (typeof value === 'number') {
    return toLocaleBrowserSettings(value);
  }

  return value ? `${value}` : undefined;
};

const getHumanizeCellText = (value: unknown) => {
  return typeof value === 'number' ? humanize(value) : null;
};

export const useSimColumnsConfiguration = () => {
  const isSessionDetailsLoading = useIsSessionDetailsListDetailsLoading();

  const SIM_COLUMNS_DEF: Record<string, ColumnsDef> = {
    iccid: {
      width: 180,
      maxWidth: 200,
      getHeaderTitle: ({ t }) => t('common.iccid'),
      cellRender: ({ column, row, ability }) => {
        const subValue = column.children.length
          ? row[column.children[0]?.name] || ''
          : row[column.children[0]?.name];
        return ability.can('read', 'simDetails') ? (
          <IccidImsiCell
            value={row[column.name]?.toString()}
            to={`${globalThis.location.pathname}/${row.iccid?.toString()}`}
            subValue={subValue}
          />
        ) : (
          <TextCell value={row[column.name]?.toString()} subValue={subValue} />
        );
      },
    },
    imsi: {
      width: 140,
      maxWidth: 140,
      getHeaderTitle: ({ t }) => t('common.imsi'),
    },
    currentIpAddress: {
      width: 130,
      maxWidth: 130,
      getHeaderTitle: ({ t }) => t('products.sim.lastKnownIP'),
      cellRender: ({ row, column }) => {
        const subValue = column.children.length
          ? row[column.children[0]?.name] || ''
          : row[column.children[0]?.name];

        return (
          <TextCell
            value={row.currentIpAddress}
            subValue={subValue}
            isValueLoading={isSessionDetailsLoading}
          />
        );
      },
    },
    managementIpAddress: {
      width: 130,
      maxWidth: 130,
      getHeaderTitle: ({ t }) => t('common.managementIp'),
    },
    mnoStatus: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('common.mnoStatus'),
    },
    msisdn: { width: 170, maxWidth: 170, getHeaderTitle: ({ t }) => t('common.msisdn') },
    liveDataUsage: {
      width: 120,
      maxWidth: 120,
      getHeaderTitle: ({ t }) => t('products.sim.dataMB'),
      getCellText: ({ value }) => getCellText(value),
    },
    inboundLiveDataUsage: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('products.sim.dataMT'),
      getCellText: ({ value }) => getCellText(value),
    },
    outboundLiveDataUsage: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('products.sim.dataMO'),
      getCellText: ({ value }) => getCellText(value),
    },
    inMinuteUsage: {
      width: 120,
      maxWidth: 120,
      getHeaderTitle: ({ t }) => t('products.sim.voiceMT'),
      getCellText: ({ value }) => getHumanizeCellText(value),
    },
    outMinuteUsage: {
      getHeaderTitle: ({ t }) => t('products.sim.voiceMO'),
      getCellText: ({ value }) => getHumanizeCellText(value),
    },
    inTextUsage: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('products.sim.smsMT'),
      getCellText: ({ value }) => getCellText(value),
    },
    outTextUsage: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('products.sim.smsMO'),
      getCellText: ({ value }) => getCellText(value),
    },
    sessionStatus: {
      width: 190,
      maxWidth: 190,
      getHeaderTitle: ({ t }) => t('simInventory.sessionStatus'),
      cellRender: ({ column, row }) => {
        return (
          <IconCell
            icon={<SessionStatusIndicator status={row.sessionStatus} />}
            primaryText={getSessionStatusTranslatedLabel(row.sessionStatus, '')}
            secondaryText={row[column.children[0]?.name]}
            isSecondaryLoading={isSessionDetailsLoading}
          />
        );
      },
    },
    sessionStatusLastUpdated: {
      width: 190,
      maxWidth: 190,
      getHeaderTitle: ({ isChildren, t }) =>
        isChildren
          ? t('simInventory.sessionStatusLastUpdated_combined')
          : t('simInventory.sessionStatusLastUpdated_flat'),
      cellRender: ({ row }) => {
        return (
          <TextCell value={row.sessionStatusLastUpdated} isValueLoading={isSessionDetailsLoading} />
        );
      },
    },
    sessionStartTime: {
      width: 200,
      maxWidth: 200,
      getHeaderTitle: ({ t }) => t('simInventory.sessionStartTime'),
    },
    sessionEndTime: {
      width: 200,
      maxWidth: 200,
      getHeaderTitle: ({ t }) => t('simInventory.sessionEndTime'),
    },
    tags: {
      width: 150,
      maxWidth: 150,
      getHeaderTitle: ({ t }) => t('common.tag'),
      cellRender: ({ row }) => {
        return <SimTagsCell row={row} />;
      },
    },
    simType: { width: 120, maxWidth: 120, getHeaderTitle: ({ t }) => t('simInventory.simType') },
    subscriptionStatus: {
      width: 180,
      maxWidth: 240,
      getHeaderTitle: ({ t }) => t('simInventory.subscriptionStatus'),
      cellRender: ({ row, column }) => {
        const subscriptionStatus = row[column.name];
        return <SubscriptionStatusCell subscriptionStatus={subscriptionStatus} />;
      },
    },
    customerCommsPlan: {
      width: 160,
      getHeaderTitle: ({ t }) => t('simInventory.customerCommsPlan'),
    },
    commsPlan: { width: 160, getHeaderTitle: ({ t }) => t('simInventory.commsPlan') },
    mobileNetworkOperator: {
      width: 160,
      getHeaderTitle: ({ t }) => t('simInventory.mobileNetworkOperator'),
    },
    organisation: { width: 160, getHeaderTitle: ({ t }) => t('common.enterprise') },
    accountName: { width: 160, getHeaderTitle: ({ t }) => t('common.accountName') },
    accountRef: { width: 160, getHeaderTitle: ({ t }) => t('common.accountNumber') },
    connectionId: { width: 120, getHeaderTitle: ({ t }) => t('simInventory.connectionId') },
    orderNumber: { width: 120, getHeaderTitle: ({ t }) => t('simInventory.orderNumber') },
    activeConfiguration: {
      width: 200,
      getHeaderTitle: ({ t }) => t('simInventory.activeConfiguration'),
    },
    installerName: { width: 160, getHeaderTitle: ({ t }) => t('simInventory.organisationName') },
    installerRef: { width: 160, getHeaderTitle: ({ t }) => t('simInventory.organisationNumber') },
    dcpEnterprise: { getHeaderTitle: ({ t }) => t('common.dcpEnterprise') },
    productCode: { getHeaderTitle: ({ t }) => t('simInventory.productCode') },
    productType: { getHeaderTitle: ({ t }) => t('simInventory.productType') },
    customerReference: { getHeaderTitle: ({ t }) => t('products.sim.orderReference') },
    mnoStatusEntryTime: {
      width: 195,
      getHeaderTitle: ({ isChildren, t }) =>
        isChildren
          ? t('simInventory.mnoStatusEntryTime_combined')
          : t('simInventory.mnoStatusEntryTime_flat'),
    },
  };

  return SIM_COLUMNS_DEF;
};
